/**
 * TypeScript mirror of the C# wire types in
 * src/lambda/shared/GoobDoobVtt.Domain/SceneMessages.cs.
 *
 * Keep in lockstep with that file. All inbound messages tolerate unknown
 * fields so adding new optional properties is backward compatible.
 */

import type { SceneElement, SceneLayer } from "../../lib/scene";
import type { MarkerPosition } from "../../lib/worldMap";

// Client → server action strings.
export const SceneActions = {
  Subscribe: "scene/subscribe",
  Unsubscribe: "scene/unsubscribe",
  Op: "scene/op",
  Cursor: "scene/cursor",
  StrokeLive: "scene/stroke-live",
  /** DM-only: tombstone every annotation (non-token, non-image) on a scene. */
  Clear: "scene/clear",
  /** DM-only: revive a set of tombstoned elements (undo for Clear). */
  Restore: "scene/restore",
} as const;

// Server → client envelope `type` values.
export const SceneServerTypes = {
  SceneState: "scene/state",
  SceneOp: "scene/op",
  Cursor: "scene/cursor",
  StrokeLive: "scene/stroke-live",
  OpRejected: "scene/op-rejected",
  /**
   * A batch of authoritative elements (post clear / restore). Handled like
   * scene/state — fold through the reconciler — but carries a `reason` so
   * the UI can toast "cleared N annotations" with an undo.
   */
  Batch: "scene/batch",
  Error: "error",
  Ack: "ack",
} as const;

/**
 * Server → client envelope `type` values for the lore feature. These ride on
 * the same WebSocket pipe as the scene types. The lore page opens its own
 * bare WS via LoreLive (SceneClient is map-scoped and shouldn't own
 * cross-cutting events), but the wire-type mirror belongs alongside the
 * rest of the protocol for parity with the C# LoreServerTypes class.
 */
export const LoreServerTypes = {
  PermsChanged: "lore/perms-changed",
} as const;

/**
 * Payload for LoreServerTypes.PermsChanged. Informational only — the client
 * decides whether a manifest refetch is warranted based on what's open.
 */
export interface LorePermsChangedPayload {
  campaignId: string;
  /** "note" (single PUT), "bulk" (folder apply), or "publish" (draft → vault). */
  kind: string;
  /** Folder or slug that changed; empty string when not path-specific. */
  path: string;
}

/**
 * Server → client envelope `type` values for the world-map feature. Ride on
 * the same WebSocket pipe as scene/lore. Mirrors the C# WorldMapServerTypes
 * class in SceneMessages.cs. Clients subscribe via {@link WorldMapLive}.
 */
export const WorldMapServerTypes = {
  MarkerRevealed: "world/marker-revealed",
  LatticeStateChanged: "world/lattice-state-changed",
} as const;

/**
 * Payload for {@link WorldMapServerTypes.MarkerRevealed}. Fired when a DM
 * edit flips a marker from hidden → visible to at least one player. Carries
 * enough for the frontend to render a click-through toast ("A new location
 * has been revealed: {label}") without another API round-trip. Clients on
 * the affected map can also refresh their marker list when handling it.
 */
export interface WorldMapMarkerRevealedPayload {
  campaignId: string;
  mapId: string;
  markerId: string;
  label: string;
  /** Present when the parent map has a slug — used for deep-link to viewer. */
  mapSlug?: string;
  position?: MarkerPosition;
}

/**
 * Payload for {@link WorldMapServerTypes.LatticeStateChanged}. Mirrors the
 * full CampaignLatticeState record so the client can apply it without a
 * round-trip GET. Strain is clamped server-side; the frontend double-clamps
 * defensively. Fires on every DM tick to the slider or the eighth-flame flag.
 */
export interface LatticeStateChangedPayload {
  campaignId: string;
  /** 0..100 — always clamped server-side. */
  latticeStrain: number;
  eighthFlameVisible: boolean;
  currentMapId?: string | null;
  updatedAt: number;
}

// -------------------- outbound (client → server) --------------------

export interface SubscribeRequest {
  action: "scene/subscribe";
  sessionId: string;
  mapId: string;
}

export interface OpRequest {
  action: "scene/op";
  mapId: string;
  element: SceneElement;
}

export interface CursorRequest {
  action: "scene/cursor";
  mapId: string;
  x: number;
  y: number;
}

export interface StrokeLiveRequest {
  action: "scene/stroke-live";
  mapId: string;
  elementId: string;
  points?: number[];
  pressures?: number[];
  strokeColor?: string;
  strokeWidth?: number;
  /**
   * Target layer for the in-progress stroke. Required for the server to
   * apply SceneVisibility.CanSee on the broadcast — without it, a
   * player-layer stroke's mid-stroke preview would leak to everyone even
   * though the committed op is filtered correctly. See SceneMessages.cs.
   */
  layer?: SceneLayer;
}

export interface ClearRequest {
  action: "scene/clear";
  mapId: string;
}

export interface RestoreRequest {
  action: "scene/restore";
  mapId: string;
  elementIds: string[];
}

// -------------------- inbound (server → client) --------------------

export interface ServerEnvelope<T = unknown> {
  type: string;
  payload?: T;
}

export interface SceneStatePayload {
  mapId: string;
  elements: SceneElement[];
}

/** Same shape as OpRequest but with an `origin` field added by the server. */
export interface SceneOpPayload {
  mapId: string;
  element: SceneElement;
  origin?: string;
}

export interface CursorPayload {
  mapId: string;
  userId: string;
  username?: string;
  x: number;
  y: number;
}

export interface StrokeLivePayload {
  mapId: string;
  elementId: string;
  userId: string;
  /**
   * Layer the stroke was authored on. Present when the server included it
   * in the rebroadcast; missing on older servers. Receivers should apply a
   * defensive visibility check when present.
   */
  layer?: SceneLayer;
  points?: number[];
  pressures?: number[];
  strokeColor?: string;
  strokeWidth?: number;
}

/** Payload for SceneServerTypes.Batch. */
export interface SceneBatchPayload {
  mapId: string;
  elements: SceneElement[];
  /** "clear" | "restore" — informational, drives the toast copy. */
  reason: string;
  /** Sub of the DM who triggered it. */
  actorId?: string;
}

export interface OpRejectedPayload {
  elementId: string;
  reason: string;
  /** Current server-authoritative element (null on non-version rejections). */
  current: SceneElement | null;
}

export interface ErrorPayload {
  code: string;
  message: string;
}
